import { ApolloQueryResult, OperationVariables } from '@parkchirp/api-client';
import { ReactNode } from 'react';

import { SetShowSnackbar } from './alerts';

export interface ProfileData {
	gpiCards: {
		id: number;
		token: string;
		last4: string;
		exp_month?: string;
		exp_year?: string;
		expMonth?: string;
		expYear?: string;
		type: string;
	}[];
	gpiBankAccounts: {
		id: number;
		firstName: string;
		lastName: string;
		token: string;
		last4: string;
		accountType: string;
	}[];
}

export interface RecurringPaymentsProps {
	useAch: boolean;
	useCreditCard: boolean;
	cognitoSub: string;
	profileData: ProfileData;
	isAutoPay: boolean;
	paymentMethodId: number | null;
	subscriptionId: string;
	manageRecurringPayments: string;
	gpiFormConfig: Record<string, any>;
	setShowSnackbar: SetShowSnackbar;
	tenantSubscriptionId?: string;
	facilityId: string;
	refetchPaymentMethods?: (variables?: Partial<OperationVariables>) => Promise<ApolloQueryResult<any>>;
	paymentProviderId: number;
}

export interface PaymentOptionProps {
	useAch: boolean;
	useCreditCard: boolean;
	editPaymentOptions: string;
	cognitoSub: string;
	wordpressUserId: number | null;
	profileData: ProfileData;
	subscriptionId: string;
	setShowSnackbar: SetShowSnackbar;
	forTenant: boolean;
	migratePayorComponent: ReactNode | null;
	gpiFormConfig: Record<string, any>;
	tenantSubscriptionId?: string;
	refetchPaymentMethods: (variables?: Partial<OperationVariables>) => Promise<ApolloQueryResult<any>>;
	paymentProviderId: number;
}

export interface ProcessPaymentProps {
	useAch: boolean;
	useCreditCard: boolean;
	adminProcessPayments: string;
	editPaymentOptions: string;
	billingProfileId: string;
	cognitoSub: string;
	subscriptionId: string;
	profileData: ProfileData;
	migratePayorComponent: ReactNode;
	setShowSnackbar: SetShowSnackbar;
	gpiFormConfig: Record<string, any>;
	invoiceData?: Record<string, any>[];
	invoiceLoading: boolean;
	refetchInvoices: () => void;
	tenantSubscriptionId?: string;
	appName: string;
}

export type ProcessSlidePayPaymentProps = {
	adminProcessPayments: string;
	appName?: string;
	billingProfileId: string;
	cognitoSub: string;
	editPaymentOptions: string;
	invoiceData?: Record<string, any>[];
	invoiceLoading: boolean;
	subscriptionId: string;
	profileData: ProfileData;
	useCreditCard: boolean;
	useAch: boolean;
	tenantSubscriptionId?: string;
	setShowSnackbar: SetShowSnackbar;
	refetchInvoices: (variables?: Partial<OperationVariables>) => Promise<ApolloQueryResult<any>>;
	refetchPaymentMethods: (variables?: Partial<OperationVariables>) => Promise<ApolloQueryResult<UserPaymentMethods>>;
};

export type UserPaymentMethods = {
	getUserPaymentMethods: {
		gpiCards: {
			id: number;
			token: string;
			last4: string;
			expMonth: string;
			expYear: string;
			type: string;
		}[];
		gpiBankAccounts: {
			id: number;
			firstName: string;
			lastName: string;
			token: string;
			last4: string;
			accountType: string;
		}[];
	};
};

export const PaymentProvidersById = Object.freeze({
	GPI: 1,
	SlidePay: 2,
});

type SlidePayEvent = Event & {
	target: EventTarget & { token: string };
};

export type HostedFieldsTokenResolverResponse = {
	getSlidePayHostedFieldsToken: {
		hostedFieldsToken: string;
	};
};

export type HostedFieldsSubmitEvent = SlidePayEvent & {
	detail: {
		result: boolean; // whether or not the form submission succeeded
		id: string; // the transaction id for the "payment"
	};
};

export type HostedFieldsErrorEvent = SlidePayEvent & {
	detail: {
		message: string;
	};
};

export type SlidePayFormConfig = {
	token: string;
	amount: number;
	fields: Record<string, any>;
	styles: Record<string, any>;
};
